// PRD 13 §3.4 / §8 — destructive confirm before permanent delete.
// Title is truncated in the confirm copy to keep the modal compact. On success
// the list query is invalidated by the mutation hook and the row disappears.

import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ConfirmModal } from '@/components/feedback/ConfirmModal';
import { InfoModal } from '@/components/feedback/InfoModal';
import { toast } from '@/components/feedback/useToast';

import { useDeleteNotice } from '../hooks/use-notice-mutations';
import type { NoticeSummary } from '../types/notices.types';
import { getNoticeErrorMessage } from '../utils/error-message';

interface DeleteNoticeConfirmProps {
  notice: NoticeSummary | null;
  onClose: () => void;
  // Called after a successful delete — lets the form view return to the list.
  onDeleted?: () => void;
}

const TITLE_MAX = 40;

export function DeleteNoticeConfirm({ notice, onClose, onDeleted }: DeleteNoticeConfirmProps) {
  const { t } = useTranslation('notices');
  const deleteMutation = useDeleteNotice();
  // PRD §8.2 — a delete API failure is shown in a popup dialog, not a toast.
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const shortTitle = notice
    ? notice.title.length > TITLE_MAX
      ? `${notice.title.slice(0, TITLE_MAX)}...`
      : notice.title
    : '';

  const handleConfirm = async () => {
    if (!notice || deleteMutation.isPending) return;
    try {
      await deleteMutation.mutateAsync(notice.id);
      toast.success(t('toasts.deleted'));
      onClose();
      onDeleted?.();
    } catch (e) {
      // Close the confirm dialog, then surface the failure in a popup (PRD §8.2).
      onClose();
      setErrorMessage(getNoticeErrorMessage(e, t, 'toasts.deleteError'));
    }
  };

  return (
    <>
      <ConfirmModal
        open={notice !== null}
        title={t('deleteConfirm.title')}
        message={notice ? t('deleteConfirm.message', { title: shortTitle }) : ''}
        confirmLabel={t('deleteConfirm.confirm')}
        cancelLabel={t('deleteConfirm.cancel')}
        confirmVariant="destructive"
        loading={deleteMutation.isPending}
        loadingLabel={t('deleteConfirm.deleting')}
        onConfirm={handleConfirm}
        onCancel={() => {
          if (deleteMutation.isPending) return;
          onClose();
        }}
      />

      <InfoModal
        open={errorMessage !== null}
        title={t('deleteConfirm.errorTitle')}
        body={errorMessage ?? ''}
        onClose={() => setErrorMessage(null)}
      />
    </>
  );
}
